import { useLayoutEffect, useSyncExternalStore } from 'react'

/**
 * Hands a page's server seed to a client component the PAGE does not render
 * (a layout-level surface like the global chat list, or a hook deep in a
 * static body). The page renders `SeedBootstrap` behind its own Suspense
 * boundary; the consumer reads `useSeed()` and paints the seed through
 * `useSeededAuthedQuery` / `useSeededAuthedPaginatedQuery`, which show it
 * only while their args are the seed's — so a seed left over from another
 * brand, record or page is never painted.
 *
 * The newest published seed wins, and only while its page is mounted: a
 * seed never outlives the page that read it, so a surface shown elsewhere
 * later (the side-panel chat list) waits for its subscription as before.
 * The server renders (and the client hydrates) without one; `SeedBootstrap`
 * publishes in a layout effect, which keeps the store write out of render
 * while the consumer still re-renders with the seed before the navigation
 * paints.
 */
export function createSeedBridge<Seed>() {
  let currentSeed: Seed | null = null
  const listeners = new Set<() => void>()

  function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  }

  function getSnapshot(): Seed | null {
    return currentSeed
  }

  function getServerSnapshot(): null {
    return null
  }

  function publish(seed: Seed | null): void {
    if (currentSeed === seed) {
      return
    }
    currentSeed = seed
    for (const listener of listeners) {
      listener()
    }
  }

  /** The mounted page's seed, or `null`. */
  function useSeed(): Seed | null {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  }

  /** Renderless: publishes `seed` for {@link useSeed} while mounted. */
  function SeedBootstrap({ seed }: { seed: Seed }) {
    useLayoutEffect(() => {
      publish(seed)
      return () => {
        if (currentSeed === seed) {
          publish(null)
        }
      }
    }, [seed])
    return null
  }

  return { useSeed, SeedBootstrap }
}
